import { z } from "zod";
import { CONSULT_STATUSES, INVITABLE_ROLES, ORG_SCOPED_ROLES } from "@telecurb/core";
import { csvParam, PageQuery, paginated, sortQuery } from "./pagination.js";
// ─── Specialties ─────────────────────────────────────────────────────────────
// Adult vs Pediatric display bucket — the two top-level groups the specialty
// picker renders under (TC-955 / TC-569).
export const SpecialtyGroup = z.enum(["Adult", "Pediatric"]);
export const SpecialtyResponse = z.object({
    id: z.string().uuid(),
    name: z.string(),
    group: SpecialtyGroup,
});
// Platform-admin specialty view (TEL-128 phase 2): includes inactive specialties,
// their Adult/Pediatric group, and the count of assigned specialist users.
// TEL-290: assignedSpecialistCount is any specialist linked to the specialty;
// routableSpecialistCount is the subset that can actually receive a routed
// consult right now (mirrors the router's own eligibility predicate) — a
// specialty can look staffed while routing nowhere.
export const SpecialtyAdminResponse = z.object({
    id: z.string().uuid(),
    name: z.string(),
    active: z.boolean(),
    group: SpecialtyGroup,
    assignedSpecialistCount: z.number().int().nonnegative(),
    routableSpecialistCount: z.number().int().nonnegative(),
});
// TEL-128 phase 3: a specialist's specialty assignment (id + name).
export const UserSpecialtyResponse = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
// TEL-128 phase 3: platform-admin adds a specialty to a specialist.
export const AssignSpecialtyRequest = z.object({
    specialtyId: z.string().uuid(),
});
// Platform admin grants a consult curator access to orgs (TEL-114, TC-1796).
// TEL-224 QA fix: `organizationIds` batches several grants into one request so
// the curator gets a single "you've been added" email; `organizationId` is the
// original single-org form (kept so a stale cached SPA keeps working).
export const CuratorOrgGrantRequest = z
    .object({
    organizationId: z.string().uuid().optional(),
    organizationIds: z.array(z.string().uuid()).min(1).max(100).optional(),
})
    .superRefine((data, ctx) => {
    if (!data.organizationId === !data.organizationIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationIds"],
            message: "provide exactly one of organizationId or organizationIds",
        });
    }
});
// Shared between the Zod schemas below and the web form's maxLength/counter so
// the client cap never drifts from the server limit.
export const SPECIALTY_NAME_MAX_LENGTH = 120;
export const CreateSpecialtyRequest = z.object({
    name: z.string().trim().min(1).max(SPECIALTY_NAME_MAX_LENGTH),
    group: SpecialtyGroup.optional().default("Adult"),
});
export const UpdateSpecialtyRequest = z
    .object({
    name: z.string().trim().min(1).max(SPECIALTY_NAME_MAX_LENGTH).optional(),
    group: SpecialtyGroup.optional(),
    active: z.boolean().optional(),
})
    .refine((d) => d.name !== undefined || d.group !== undefined || d.active !== undefined, {
    message: "at least one field is required",
});
export const MeResponse = z.object({
    // Opaque DB user id — the web layer compares it against
    // AttachmentResponse.uploadedBy to gate owner-only controls (TEL-101).
    id: z.string().uuid(),
    email: z.string().email().nullable(),
    displayName: z.string(),
    role: z.string().nullable(),
    organizationId: z.string().uuid().nullable(),
    // Org display name for org-scoped roles (practice_admin/pcp/css); null for
    // platform_admin/specialist/curator, who have no single org. Drives the
    // practice-admin header subtitle (TEL-250).
    organizationName: z.string().nullable(),
});
// ─── Auth ────────────────────────────────────────────────────────────────────
export const LoginRequest = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const ChangePasswordRequest = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(1),
});
export const InviteLookupQuery = z.object({
    token: z.string().min(1),
});
export const InviteLookupResponse = z.object({
    email: z.string().email(),
    pending: z.boolean(),
});
// TEL-169: query for the public password-reset redeem endpoint. No response
// DTO — the endpoint only ever answers with a 302 redirect.
export const PasswordResetRedeemQuery = z.object({
    token: z.string().min(1),
});
// TEL-312: body for the public self-service request endpoint — takes email
// only; always returns 200 regardless of whether the email is registered.
export const PasswordResetRequestBody = z.object({
    email: z.string().email(),
});
// TEL-206: body for the public complete-reset endpoint, posted from our own
// /reset-password page. Password strength is enforced by WorkOS at the
// provider call (→ 422 weak_password), not duplicated here.
export const PasswordResetCompleteRequest = z.object({
    token: z.string().min(1),
    password: z.string().min(1),
});
// Accepts any reasonable US formatting (digits, spaces, dashes, dots, parens,
// optional +1 prefix). Strips non-digits and requires exactly 10 (or 11 with
// leading 1) digits — covers every common US format without over-constraining.
// The national number must not start with 0 or 1 (NANP area codes are 2-9).
// This also catches 9-digit partial input arriving in E.164 form ("+1" + 9
// digits = 10 digits total), which a bare length check would wrongly accept.
export function isValidUSPhone(val) {
    const d = val.replace(/\D/g, "");
    const national = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
    return national.length === 10 && /^[2-9]/.test(national);
}
// Inline-validation predicate for phone inputs: true when the field holds a
// value that isn't a valid US number. Partial input is covered too:
// react-phone-number-input emits a partial E.164 value (e.g. "+1734") for any
// digits, and undefined only when the field is empty.
export function hasPhoneInlineError(phone) {
    return !!phone && !isValidUSPhone(phone);
}
// Converts any valid US phone format to E.164 (+1XXXXXXXXXX) for consistent storage and Twilio compatibility.
export function normalizeUSPhone(val) {
    const d = val.replace(/\D/g, "");
    const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
    return `+1${ten}`;
}
const usPhone = z
    .string()
    .refine(isValidUSPhone, { message: "Must be a valid US phone number" })
    .transform(normalizeUSPhone);
export const AcceptInviteRequest = z.object({
    token: z.string().min(1),
    password: z.string().min(1),
    displayName: z.string().min(1).optional(),
    phone: usPhone.optional(),
    notifySms: z.boolean().optional(),
});
// The kind of second factor a user has enrolled (TEL-170). 'sms' = WorkOS-
// delivered one-time-passcode text; 'totp' = an authenticator app (Google
// Authenticator, Microsoft Authenticator, Authy, 1Password, …). A user holds
// exactly one at a time.
export const MfaFactorType = z.enum(["sms", "totp"]);
// MFA (TEL-112). Login is two-step: a correct password never seals a session
// directly — it returns one of these statuses and the client completes the
// challenge. Kept a discriminatedUnion so Phase 2's trusted-device shortcut can
// add a third `{ status: "authenticated" }` variant without breaking clients.
// (Defined after usPhone, which MfaEnrollRequest reuses.)
export const LoginResponse = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("mfa_required"),
        challengeId: z.string(),
        expiresAt: z.string().nullable(),
        // TEL-170: which factor is enrolled, so the challenge UI adapts — an SMS
        // factor shows "code sent / Resend"; a TOTP factor reads the code from the
        // authenticator app (no send, no resend).
        factorType: MfaFactorType,
    }),
    z.object({ status: z.literal("mfa_enrollment_required") }),
    // Phase 2: a trusted device (valid tc_trusted_device cookie) skips the second
    // factor — the session is sealed at login and the client enters the app directly.
    z.object({ status: z.literal("authenticated") }),
]);
// Enrollment request (TEL-170). Discriminated on `method`: SMS needs a US phone;
// TOTP needs nothing (the server enrolls a factor and returns a QR to scan).
export const MfaEnrollRequest = z.discriminatedUnion("method", [
    z.object({ method: z.literal("sms"), phone: usPhone }).strict(),
    z.object({ method: z.literal("totp") }).strict(),
]);
// Returned by enroll. Always carries the first challenge id; for a TOTP
// enrollment it additionally carries the one-time QR (a data URL to render) and
// the secret for manual key entry — shown once and never returned again.
export const MfaEnrollResponse = z.object({
    challengeId: z.string(),
    expiresAt: z.string().nullable(),
    totp: z.object({ qrCode: z.string(), secret: z.string() }).optional(),
});
// Returned by the resend-challenge endpoint (SMS only — TOTP never resends).
export const MfaChallengeResponse = z.object({
    challengeId: z.string(),
    expiresAt: z.string().nullable(),
});
export const MfaVerifyRequest = z
    .object({
    challengeId: z.string().min(1),
    code: z.string().regex(/^[0-9]{6}$/),
    // Phase 2: "Remember this device" — when true, a trusted-device token is
    // minted on success so future logins from this browser skip the SMS factor.
    rememberDevice: z.boolean().optional(),
})
    .strict();
export const MfaVerifyResponse = z.object({ status: z.literal("authenticated") });
export const UpdateProfileRequest = z
    .object({
    displayName: z.string().min(1).optional(),
    phone: usPhone.nullable().optional(),
    notifyEmail: z.boolean().optional(),
    notifySms: z.boolean().optional(),
    // TEL-109: specialist-only universal availability toggle. A non-specialist
    // who sends this is rejected 422 by the profile service.
    acceptingConsults: z.boolean().optional(),
})
    .strict();
export const ProfileResponse = z.object({
    email: z.string().email().nullable(),
    displayName: z.string(),
    phone: z.string().nullable(),
    notifyEmail: z.boolean(),
    notifySms: z.boolean(),
    acceptingConsults: z.boolean(),
    role: z.string(),
    specialties: z.array(z.string()).default([]),
});
export const HealthResponse = z.object({
    ok: z.literal(true),
    service: z.string(),
    version: z.string(),
});
export const CreateOrganizationRequest = z.object({
    name: z.string().min(1).max(255),
    slug: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9-]+$/, "slug must be lowercase alphanumeric with hyphens"),
});
export const OrganizationResponse = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    isActive: z.boolean(),
    contactEmail: z.string().email().nullable(),
    contactPhone: z.string().nullable(),
    address: z.string().nullable(),
});
export const UpdateOrganizationRequest = z
    .object({
    name: z.string().min(1).max(255).optional(),
    contactEmail: z.string().email().nullable().optional(),
    contactPhone: z.string().max(30).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
})
    .strict();
export const CreateInviteRequest = z
    .object({
    email: z.string().email(),
    displayName: z.string().min(1).max(255),
    role: z.enum(INVITABLE_ROLES),
    organizationId: z.string().uuid().optional(),
    // TEL-224: consult curators can be credentialed into several orgs at
    // invite time. Curator-only; other roles keep the single organizationId.
    organizationIds: z.array(z.string().uuid()).min(1).max(100).optional(),
    specialtyId: z.string().uuid().optional(),
    // TEL-225: specialists can hold several specialties (fellowships;
    // allergist/immunologists serve both peds and adults). Specialist-only;
    // same seam as the curator organizationIds above.
    specialtyIds: z.array(z.string().uuid()).min(1).max(100).optional(),
})
    .superRefine((data, ctx) => {
    const isOrgScoped = ORG_SCOPED_ROLES.includes(data.role);
    // org-scoped roles require an organizationId
    if (isOrgScoped && !data.organizationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationId"],
            message: "organizationId is required for org-scoped roles",
        });
    }
    // organizationIds is the curator-only multi-org form (TEL-224)
    if (data.organizationIds && data.role !== "consult_curator") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationIds"],
            message: "organizationIds is only valid for the consult_curator role",
        });
    }
    // one form or the other, never both
    if (data.organizationIds && data.organizationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationIds"],
            message: "provide either organizationId or organizationIds, not both",
        });
    }
    // specialist: org forbidden, specialtyId required
    if (data.role === "specialist" && data.organizationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationId"],
            message: "organizationId must be omitted for specialist role",
        });
    }
    // platform_admin (TEL-171): a global role — org forbidden (specialtyId is
    // already rejected below by the non-specialist rule).
    if (data.role === "platform_admin" && data.organizationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["organizationId"],
            message: "organizationId must be omitted for platform_admin role",
        });
    }
    if (data.role === "specialist" && !data.specialtyId && !data.specialtyIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["specialtyId"],
            message: "specialtyId or specialtyIds is required for specialist role",
        });
    }
    // specialtyIds is the specialist-only multi-specialty form (TEL-225)
    if (data.specialtyIds && data.role !== "specialist") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["specialtyIds"],
            message: "specialtyIds is only valid for the specialist role",
        });
    }
    // one form or the other, never both
    if (data.specialtyIds && data.specialtyId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["specialtyIds"],
            message: "provide either specialtyId or specialtyIds, not both",
        });
    }
    // non-specialist roles must not include specialtyId
    if (data.role !== "specialist" && data.specialtyId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["specialtyId"],
            message: "specialtyId must be omitted for non-specialist roles",
        });
    }
});
export const InternalUserResponse = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    role: z.string(),
    status: z.enum(["invited", "active", "deactivated"]),
    specialties: z.array(z.string()),
    // TEL-109: universal availability flag. Only meaningful for specialists;
    // defaults true for everyone else.
    acceptingConsults: z.boolean(),
});
// ─── Platform user directory (GET /v1/users) — server-paginated ─────────────
const USER_ROLES = [
    "platform_admin",
    "practice_admin",
    "pcp",
    "clinical_support_staff",
    "consult_curator",
    "specialist",
];
export const USER_SORT_KEYS = ["name", "email", "role", "status"];
export const ListUsersQuery = z
    .object({
    role: z.enum(USER_ROLES).optional(),
    status: z.enum(["invited", "active", "deactivated"]).optional(),
    orgId: z.string().uuid().optional(),
    specialtyId: z.string().uuid().optional(),
    // Name / email / specialty / org name — a match predicate only, never logged.
    q: z.string().trim().max(200).optional(),
})
    .merge(PageQuery)
    .merge(sortQuery(USER_SORT_KEYS, { sort: "name", dir: "asc" }));
// A directory row (the Users page table + detail drawer).
export const UserDirectoryResponse = InternalUserResponse.extend({
    hasLoggedIn: z.boolean(),
    organizationName: z.string().nullable(),
    organizationNames: z.array(z.string()),
});
export const ListUsersResponse = paginated(UserDirectoryResponse);
// GET /v1/users/options — every user as id + name + role, for pickers and
// id → name maps (the audit log's actor filter) that need the whole set but
// not the paged directory's hydrated rows.
export const UserOptionResponse = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    role: z.string(),
});
// TEL-109: platform-admin patch of a specialist's universal availability flag.
export const UpdateUserRequest = z.object({ acceptingConsults: z.boolean() }).strict();
// NEW-21: platform-admin change of a user's login email.
export const ChangeEmailRequest = z.object({ email: z.string().email() });
// Platform Testing 2: display names flow into clinical documents, so only a
// platform or practice admin sets them (on invite) and changes them (here).
export const ChangeDisplayNameRequest = z.object({
    displayName: z.string().trim().min(1).max(255),
});
export const InviteResponse = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
    role: z.string(),
    organizationId: z.string().uuid().optional(),
    // TEL-224: set instead of organizationId when a curator was invited into
    // multiple orgs.
    organizationIds: z.array(z.string().uuid()).optional(),
    status: z.literal("invited"),
});
export const MemberResponse = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    role: z.string(),
    status: z.string(),
    joinedAt: z.string().datetime().nullable(),
});
// ─── Patients ────────────────────────────────────────────────────────────────
export const SexAtBirth = z.enum(["female", "male", "intersex", "prefer_not_to_say"]);
// Full patient response — used for POST /v1/patients and as the typed shape
// callers use when working with a hydrated patient.
export const PatientResponse = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    mrn: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    // Nullable on read — legacy/integration rows may lack it. Required on create.
    sexAtBirth: SexAtBirth.nullable(),
    dateOfBirth: z.string().date().nullable(), // YYYY-MM-DD; nullable per schema.ts
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// Subset of patient fields embedded in ConsultResponse to avoid a follow-up fetch.
// Identifiers (mrn/firstName/lastName/dateOfBirth) are null on specialist-served
// payloads — the API redacts them server-side and sends only age + sex (TEL-181,
// HIPAA minimum-necessary). Non-specialist roles get them populated as before.
export const PatientSummary = z.object({
    id: z.string().uuid(),
    mrn: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    sexAtBirth: SexAtBirth.nullable(),
    dateOfBirth: z.string().date().nullable(),
    // Full years, derived server-side from dateOfBirth. Null only when the birth
    // date is unrecorded. Populated for every role (specialists see this, not DOB).
    age: z.number().int().nonnegative().nullable(),
});
// Body for POST /v1/patients/lookup. The MRN is PHI, so it must travel in a
// request body — never a query string, where managed request logging (Cloud
// Run) would capture it before app-level redaction runs (TEL-234).
export const PatientLookupRequest = z.object({
    orgId: z.string().uuid(),
    mrn: z.string().min(1),
});
// Absence is expected in the wizard's lookup-or-create flow — return null,
// not 404. 404 is reserved for routes where missingness is an error.
export const PatientLookupResponse = z.object({
    patient: PatientResponse.nullable(),
});
// Upper bound on MRN length. The wizard's Patient step enforces the same cap
// inline (TEL-252) — keep the two in sync via this constant.
export const MAX_MRN_LENGTH = 64;
export const CreatePatientRequest = z.object({
    organizationId: z.string().uuid(),
    mrn: z.string().min(1).max(MAX_MRN_LENGTH),
    firstName: z.string().min(1).max(128),
    lastName: z.string().min(1).max(128),
    sexAtBirth: SexAtBirth,
    dateOfBirth: z
        .string()
        .date()
        .refine((d) => {
        const today = new Date();
        const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        return d <= ymd;
    }, { message: "Date of birth cannot be in the future" }),
});
// ─── Consults ────────────────────────────────────────────────────────────────
export const CreateConsultRequest = z.object({
    patientId: z.string().uuid(),
    organizationId: z.string().uuid(),
    specialtyId: z.string().uuid().optional(),
    history: z.string().max(20000).optional(),
    question: z.string().max(5000).optional(),
    // Optional on the wire because PCPs self-submitting don't have to send it —
    // the service fills in actor.userId. CSS / curator paths still send this.
    onBehalfOfPcpId: z.string().uuid().optional(),
});
// .strict() rejects unknown keys — guards against attempts to PATCH status,
// submittedBy, organizationId, attestation columns, etc.
// onBehalfOfPcpId is non-nullable in the PATCH: a draft may not have one yet
// (TEL-114 QA), and you can set or change which PCP — but you cannot clear it
// back to null once set.
export const UpdateConsultRequest = z
    .object({
    patientId: z.string().uuid().optional(),
    specialtyId: z.string().uuid().nullable().optional(),
    history: z.string().max(20000).nullable().optional(),
    question: z.string().max(5000).nullable().optional(),
    onBehalfOfPcpId: z.string().uuid().optional(),
})
    .strict();
// POST /consults/:id/submit body (TEL-284). `attested` is the curator's
// signed-note attestation — required (true) when a consult_curator submits;
// ignored for every other role, so PCP/CSS/platform_admin submits can keep
// sending no body at all.
export const SubmitConsultRequest = z.object({
    attested: z.boolean().optional(),
});
// Statuses the list endpoint accepts. Now the full lifecycle: PCP "Pending" is
// the in-flight union submitted | assigned | accepted, and the post-response
// states (answered / follow-up-active / closed) back the History view. Single
// source of truth for the Zod schema below and the defense-in-depth check in
// apps/api/src/services/consult.service.ts.
export const LISTABLE_CONSULT_STATUSES = [
    "draft",
    "submitted",
    "assigned",
    "accepted",
    "answered",
    "follow-up-active",
    "clarification-requested",
    "closed",
    "cancelled",
];
// `status` accepts a single value (?status=submitted) or a comma-separated
// list (?status=submitted,assigned,accepted). After parse, callers always
// see a non-empty array — keeps the downstream service shape uniform. Shared
// between the list and export queries so the parsing can't drift.
const consultStatusListParam = z.preprocess((val) => (typeof val === "string" ? val.split(",").map((s) => s.trim()) : val), z.array(z.enum(LISTABLE_CONSULT_STATUSES)).min(1));
export const ListConsultsQuery = z.object({
    status: consultStatusListParam,
    orgId: z.string().uuid().optional(),
    // TEL-115 P3: admin-view filter — only consults with a consult.rerouted audit
    // event inside the platform dashboard window. Backs the Rerouted tile's
    // ?view=rerouted deep link; ignored on non-admin list paths.
    rerouted: z
        .preprocess((val) => (typeof val === "string" ? val === "true" : val), z.boolean())
        .optional(),
    // TEL-116: "org" widens a PCP's list to every consult in the org (Org History
    // + global search). No-op for CSS/curator/practice_admin, who are already
    // org-wide. Omitted ⇒ "mine" — the PCP sees only their own consults.
    scope: z.enum(["mine", "org"]).optional(),
});
// ─── Submitter tabs (PCP / CSS / curator): buckets, counts, search ───────────
// Which tab each consult status lives on (TEL-217). One definition shared by
// the web lists, their counters and the server-side counts endpoint, so a tab
// and its count can never disagree on where a status belongs.
export const SUBMITTER_TAB_STATUSES = {
    // A response (or clarification request) the submitter must act on.
    inbox: ["answered", "clarification-requested"],
    drafts: ["draft"],
    // Awaiting a specialist: the initial wait plus an open follow-up.
    pending: ["submitted", "assigned", "accepted", "follow-up-active"],
    // The finished archive.
    completed: ["closed", "cancelled"],
};
// GET /v1/consults/counts?orgId= — the tab counters, without downloading the
// lists they count.
export const SubmitterConsultCountsQuery = z.object({ orgId: z.string().uuid().optional() });
export const SubmitterConsultCountsResponse = z.object({
    inbox: z.number().int(),
    drafts: z.number().int(),
    pending: z.number().int(),
    completed: z.number().int(),
});
// GET /v1/consults/search — the submitter global search (TEL-193): every
// non-draft consult in the org plus the viewer's own drafts, matched and
// sorted server-side and paginated. Sort keys mirror the search view's picker.
export const SUBMITTER_SEARCH_SORT_KEYS = [
    "activity",
    "patient",
    "specialty",
    "provider",
    "status",
];
export const SearchSubmitterConsultsQuery = z
    .object({
    orgId: z.string().uuid().optional(),
    // May contain an MRN — a DB match predicate only, never logged/audited.
    q: z.string().trim().min(1).max(200),
})
    .merge(PageQuery)
    .merge(sortQuery(SUBMITTER_SEARCH_SORT_KEYS, { sort: "activity", dir: "desc" }));
// GET /v1/consults/export filters (TEL-111 QA fix 3): the export mirrors the
// All Consults list's active filters. Same `status` shape as ListConsultsQuery
// but optional — no filters means the full non-draft export (the original
// behavior). `orgId` is the platform-admin org dropdown (practice_admin is
// always locked to their own org server-side, param ignored). `q` is the
// free-text search box — it may contain anything the user typed (even an MRN),
// so it is only ever used as a DB match predicate and must NEVER be written to
// logs or the audit payload.
export const ExportConsultsQuery = z.object({
    status: consultStatusListParam.optional(),
    orgId: z.string().uuid().optional(),
    q: z.string().max(200).optional(),
    // Practice-admin date range: full ISO instants, not bare YYYY-MM-DD — the
    // client computes the boundary from the wall-clock date the admin picked
    // (matching what the on-screen list itself filters by) and sends the exact
    // resulting instant. A bare date re-derived as UTC midnight server-side
    // would disagree with the list's local-timezone boundary for anyone outside
    // UTC (TEL-NEW48 QA fix).
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    // Practice-admin specialty filter — exact specialty name from the dropdown.
    specialty: z.string().max(200).optional(),
});
// Specialist outcome tags (TEL-85): the structured plan-of-care selection on the
// specialist response. Canonical id list + labels live in
// apps/web/src/components/telecurb/outcome-tag.tsx (the `Outcome` union); these
// codes must stay in sync with that file AND the consult_outcome_tags_tag_check
// DB constraint.
export const OUTCOME_TAG_CODES = [
    "medication-change",
    "lifestyle",
    "diagnostics",
    "continue",
    "clarifying-question",
    "external-referral",
    "ed-referral",
];
export const OutcomeTagCode = z.enum(OUTCOME_TAG_CODES);
export const ConsultResponse = z.object({
    id: z.string().uuid(),
    status: z.enum(CONSULT_STATUSES),
    organizationId: z.string().uuid(),
    patient: PatientSummary,
    specialtyId: z.string().uuid().nullable(),
    specialtyName: z.string().nullable(),
    history: z.string().nullable(),
    question: z.string().nullable(),
    submittedBy: z.string().uuid(),
    submittedByName: z.string(),
    // Null only on a CSS/curator draft saved before the PCP is picked (TEL-114
    // QA); required at submission, so every non-draft consult carries both.
    onBehalfOfPcpId: z.string().uuid().nullable(),
    onBehalfOfPcpName: z.string().nullable(),
    assignedSpecialistId: z.string().uuid().nullable(),
    // Display name of the assigned specialist — populated on the single-consult
    // detail fetch (drives the follow-up thread author label); null on list/admin
    // paths and before assignment.
    assignedSpecialistName: z.string().nullable(),
    // Author of the primary response. Stable across acknowledged reassignment,
    // unlike assignedSpecialistName, which represents the current assignment.
    respondedByName: z.string().nullable(),
    slaDueAt: z.string().datetime().nullable(),
    // Phase deadlines (TEL-17-E). Specialist countdowns read these, not slaDueAt:
    // acceptanceDueAt while assigned, responseDueAt while accepted,
    // followupResponseDueAt while follow-up-active.
    acceptanceDueAt: z.string().datetime().nullable(),
    responseDueAt: z.string().datetime().nullable(),
    followupResponseDueAt: z.string().datetime().nullable(),
    submittedAt: z.string().datetime().nullable(),
    respondedAt: z.string().datetime().nullable(),
    responseText: z.string().nullable(),
    needsAdminRouting: z.boolean(),
    // EMR-upload tracking (TEL-114). `emrTrackingEnabled` is true when a
    // consult_emr_uploads row exists. A row is created at submit for
    // curator-managed orgs, and on the first toggle for any other eligible org
    // (TEL-110 upsert-on-toggle) — so this is NOT a curator-only flag. The two
    // timestamps are the initial-response and close-summary upload milestones;
    // null = not yet uploaded. All three are populated from a LEFT JOIN on
    // consult_emr_uploads — false/null when there is no row.
    emrTrackingEnabled: z.boolean(),
    emrInitialUploadedAt: z.string().datetime().nullable(),
    emrCloseUploadedAt: z.string().datetime().nullable(),
    // When the org last reviewed this consult's specialist response (TC-279).
    // Null = never reviewed. The inbox treats a response as NEW (bold, top) when
    // this is null or earlier than respondedAt; reviewed ones are de-emphasized.
    responseReviewedAt: z.string().datetime().nullable(),
    // Newest specialist message (primary response, follow-up reply or
    // clarification request). The inbox NEW flag re-arms when this is newer than
    // responseReviewedAt, and "Date responded" reads it; respondedAt stays the
    // first-response time (Platform Testing 2).
    lastSpecialistMessageAt: z.string().datetime().nullable(),
    attestedBy: z.string().uuid().nullable(),
    attestedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    // Real last activity (Platform Testing 2): moved only by messages and
    // non-routing status transitions — never by background writes, unlike
    // updatedAt. Every "Last activity" label and sort reads this.
    lastActivityAt: z.string().datetime(),
    // When the consult was manually closed (mirrors closeout.closedAt). Unlike
    // `closeout`, this is populated on the list paths too (TEL-142) so the PCP
    // History list can show the close time on CLOSED rows instead of the answered
    // time. Null when the consult has no consult_closeouts row.
    closedAt: z.string().datetime().nullable(),
    // Close-out answers (TEL-84). Populated only on the single-consult detail
    // fetch when a consult_closeouts row exists; null on the list/admin paths and
    // on consults that haven't been manually closed.
    closeout: z
        .object({
        changedManagement: z.boolean(),
        referralNeeded: z.boolean(),
        starRating: z.number().int().min(1).max(5).nullable(),
        closedBy: z.string().uuid(),
        closedAt: z.string().datetime(),
    })
        .nullable(),
    // Specialist outcome tags (TEL-85). Populated on the single-consult detail
    // fetch for specialist / platform_admin / practice_admin (own-org); [] on
    // list/admin paths and for roles RLS hides them from (PCP / CSS / curator).
    // Rendered as chips only in the specialist + platform-admin views —
    // practice_admin's surface is the TEL-111 CSV export, not the in-app view.
    outcomeTags: z.array(OutcomeTagCode),
    // Specialist response draft (TEL-157), populated only on the specialist
    // single-consult detail fetch for the authoring assigned specialist — it
    // hydrates the composer in one fetch (no separate GET). Null on list/admin/
    // org paths and for every other role (RLS hides the row). The queue-level
    // "has a draft" indicator lives on SpecialistQueueItemResponse.hasDraft.
    draft: z
        .object({
        responseText: z.string().nullable(),
        outcomeTags: z.array(OutcomeTagCode),
        updatedAt: z.string().datetime(),
    })
        .nullable(),
});
export const ListConsultsResponse = z.object({
    consults: z.array(ConsultResponse),
});
export const SearchSubmitterConsultsResponse = paginated(ConsultResponse);
// EMR-upload toggle (TEL-114). Flips one milestone on a curator-managed consult.
// `uploaded: true` stamps the milestone now; `false` clears it back to null.
export const EmrUploadToggleRequest = z.object({
    milestone: z.enum(["initial", "close"]),
    uploaded: z.boolean(),
});
// ─── Central Queue (TEL-123) ────────────────────────────────────────────────
//
// Platform-admin-only cross-org routing-issue surface. The admin variant of a
// consult row carries the org name plus a derived `routing` block; the reason
// is computed in the service layer from exclusions + routing audit events (no
// schema column). See specs/tel-123-central-queue.md.
// `sla-breach` is reserved for the future breach-return ticket and is never
// produced today — the enum carries it so the wire contract is forward-stable.
export const CONSULT_ROUTING_REASONS = [
    "new",
    "declined",
    "rerouted",
    "surfaced",
    "no-eligible-specialist",
    "error",
    "sla-breach",
];
export const ConsultRoutingReason = z.enum(CONSULT_ROUTING_REASONS);
// Structured, availability-based decline reasons. Single source of truth shared
// by the specialist decline dialog and the admin routing panel. Must stay in
// sync with the consult_specialist_exclusions_reason_code_check DB constraint.
export const DECLINE_REASON_CODES = ["unavailable", "on-leave", "at-capacity", "other"];
export const DeclineReasonCode = z.enum(DECLINE_REASON_CODES);
export const DECLINE_REASON_LABELS = {
    unavailable: "Unavailable",
    "on-leave": "On Leave",
    "at-capacity": "At Capacity",
    other: "Other",
};
export const ConsultExclusionEntry = z.object({
    specialistId: z.string().uuid(),
    specialistName: z.string(),
    excludedSpecialtyId: z.string().uuid().nullable(), // null = decline, set = reroute
    excludedSpecialtyName: z.string().nullable(),
    reasonCode: DeclineReasonCode.nullable(), // set for structured declines; null for legacy/reroute/escalate
    reason: z.string().nullable(), // decline details (optional) or reroute/escalate free text
    createdAt: z.string().datetime(),
});
export const AdminConsultRouting = z.object({
    reason: ConsultRoutingReason,
    attempts: z.number().int().nonnegative(), // exclusion-row count
    // When the consult entered the backup queue (TEL-132): the operative routing
    // signal's time (newest exclusion or latest routing failure event). Null iff
    // reason === "new" (never routed, so no queue-entry moment).
    queuedAt: z.string().datetime().nullable(),
    exclusions: z.array(ConsultExclusionEntry), // newest first
});
// Platform-admin force-close reason codes (TEL-NEW53). Kept in sync with the
// consults_force_close_reason_code_check DB constraint.
export const FORCE_CLOSE_REASON_CODES = [
    "specialist-unreachable",
    "resolved-outside-platform",
    "org-request",
    "other",
];
export const ForceCloseReasonCode = z.enum(FORCE_CLOSE_REASON_CODES);
export const FORCE_CLOSE_REASON_LABELS = {
    "specialist-unreachable": "Specialist unreachable",
    "resolved-outside-platform": "Resolved outside platform",
    "org-request": "Org request",
    other: "Other",
};
// Populated only when a consult was admin force-closed (TEL-NEW53).
export const AdminConsultForceClose = z.object({
    reasonCode: ForceCloseReasonCode,
    note: z.string().nullable(),
    closedByName: z.string(),
});
export const ForceCloseConsultRequest = z.object({
    reasonCode: ForceCloseReasonCode,
    note: z.string().optional(),
});
// Platform-admin cancellation reason codes (TEL-115). Kept in sync with the
// consults_cancellation_reason_code_check DB constraint.
export const CANCEL_CONSULT_REASON_CODES = [
    "no-eligible-specialist",
    "wrong-record-attached",
    "duplicate",
    "submitted-in-error",
    "other",
];
export const CancelConsultReasonCode = z.enum(CANCEL_CONSULT_REASON_CODES);
// Human-readable labels for the cancel reason picker (mirrors DECLINE_REASON_LABELS).
export const CANCEL_CONSULT_REASON_LABELS = {
    "no-eligible-specialist": "No eligible specialist",
    "wrong-record-attached": "Wrong record attached",
    duplicate: "Duplicate consult",
    "submitted-in-error": "Submitted in error",
    other: "Other",
};
// Populated only when a consult was admin-cancelled (TEL-115). note is the
// optional admin free-text; reasonCode is the structured code.
export const AdminConsultCancellation = z.object({
    reasonCode: CancelConsultReasonCode,
    note: z.string().nullable(),
});
export const AdminConsultResponse = ConsultResponse.extend({
    organizationName: z.string(),
    routing: AdminConsultRouting,
    cancellation: AdminConsultCancellation.nullable(),
    forceClose: AdminConsultForceClose.nullable(),
    // Has a genuine consult.rerouted event (specialty actually changed) within
    // the trailing platform-dashboard window — the exact predicate + window the
    // Rerouted tile counts (queries.ts reroutedSinceCondition), so the All
    // Consults page's Rerouted attention signal can match the tile exactly.
    // Distinct from routing.reason === "rerouted", which only reflects
    // specialist-declined reroutes (exclusion rows) — admin reroutes
    // deliberately write no exclusion row (TEL-115).
    rerouted: z.boolean(),
});
// ─── Admin consult list: server-side filter / sort / page ────────────────────
// The All Consults page (platform_admin + practice_admin) used to fetch every
// consult and filter/sort/slice in the browser. These are those same controls,
// evaluated in SQL (listConsultsAsAdmin → pageConsultsCrossOrg).
// Platform-admin attention chips. Each maps 1:1 to a SQL predicate in
// queries.ts (adminAttentionCondition) that matches the dashboard tile it
// deep-links from; multiple selected signals are OR'd.
export const ADMIN_ATTENTION_SIGNALS = [
    "unassigned",
    "issue-reported",
    "rerouted",
    "16h-warning",
    "response-breach",
    "followup-breach",
];
export const AdminAttentionSignal = z.enum(ADMIN_ATTENTION_SIGNALS);
// Sortable columns. `patient` is first-then-last name (the platform-admin
// table's display order); `patientLast` is last-then-first (practice admin).
export const ADMIN_CONSULT_SORT_KEYS = [
    "org",
    "specialty",
    "patient",
    "patientLast",
    "pcp",
    "submittedBy",
    "specialist",
    "status",
    "submittedAt",
    "lastActivity",
];
export const ListAdminConsultsQuery = z
    .object({
    status: consultStatusListParam,
    orgId: z.string().uuid().optional(),
    rerouted: z
        .preprocess((val) => (typeof val === "string" ? val === "true" : val), z.boolean())
        .optional(),
    // Free-text search. Like the export's `q`, it may hold anything the user
    // typed (even an MRN) — a DB match predicate only, never logged/audited.
    q: z.string().trim().max(200).optional(),
    // Platform admin filters by specialty id; practice admin by exact name
    // (the same `specialty` param the export takes).
    specialtyId: z.string().uuid().optional(),
    specialty: z.string().max(200).optional(),
    // Platform admin "Last N days" range, resolved against the server clock.
    submittedWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
    // Practice admin From/To — exact instants computed client-side from the
    // admin's local calendar day (see ExportConsultsQuery for why not dates).
    submittedFrom: z.string().datetime().optional(),
    submittedTo: z.string().datetime().optional(),
    attention: csvParam(AdminAttentionSignal).optional(),
    // Exact consult — resolves a deep-linked detail sheet (?consult=<id>,
    // admin alert emails) whose row isn't on the page being viewed.
    consultId: z.string().uuid().optional(),
})
    .merge(PageQuery)
    .merge(sortQuery(ADMIN_CONSULT_SORT_KEYS, { sort: "lastActivity", dir: "desc" }));
export const ListAdminConsultsResponse = paginated(AdminConsultResponse);
// TEL-147: decline metadata carried by an eligibility entry when the specialist
// previously declined this consult. Their exclusion rows are all structured
// declines (reasonCode-bearing), so a platform admin may still assign them
// manually — the UI badges them "Declined · <reason> · <ago>". The auto-router
// never sees these entries (it keeps its own strict candidate filter).
export const EligibleSpecialistDecline = z.object({
    reasonCode: DeclineReasonCode,
    declinedAt: z.string(), // ISO timestamp of the newest decline row
});
export const EligibleSpecialist = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    // Null for a plain eligible specialist; set when included despite a
    // decline-only exclusion (manual-assign override surface only, TEL-147).
    declined: EligibleSpecialistDecline.nullable(),
});
export const EligibleSpecialistsResponse = z.object({
    specialists: z.array(EligibleSpecialist),
});
export const AssignConsultRequest = z.object({
    specialistId: z.string().uuid(),
});
export const AssignConsultResponse = z.object({
    consult: AdminConsultResponse,
});
// ── Platform-admin manual consult actions (TEL-115) ──────────────────────────
// Full specialty roster for the admin picker: every *active* specialist in the
// specialty, including excluded / not-accepting ones (flagged) so the admin can
// override. Deactivated accounts are excluded server-side — account status is
// not an overridable flag (they can't log in).
export const AssignableSpecialist = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    excluded: z.boolean(), // on the per-consult exclusion list
    excludedReason: z.string().nullable(),
    accepting: z.boolean(), // users.acceptingConsults
});
export const AssignableSpecialistsResponse = z.object({
    specialists: z.array(AssignableSpecialist),
});
export const ReassignConsultRequest = z.object({
    specialistId: z.string().uuid(),
    acknowledgeAnswered: z.boolean().optional().default(false),
});
// Follow-up reassignment (distinct from the general reassign above): restricted
// to follow-up-active consults, where "already answered" is implicit rather
// than an admin-toggled acknowledgement. overrideReason is a client-side
// justification for picking an excluded specialist — validated here but not
// currently persisted (mirrors the general reassign endpoint, which collects
// no reason at all).
export const ReassignFollowupConsultRequest = z.object({
    specialistId: z.string().uuid(),
    overrideReason: z.string().max(500).optional(),
});
// Admin reroute (TEL-115) — distinct from the specialist-initiated
// RerouteConsultRequest (targetSpecialtyId + reason) below. The admin picks the
// new specialty AND the specific specialist.
export const AdminRerouteConsultRequest = z.object({
    specialtyId: z.string().uuid(),
    specialistId: z.string().uuid(),
    acknowledgeAnswered: z.boolean().optional().default(false),
});
export const CancelConsultRequest = z.object({
    reasonCode: CancelConsultReasonCode,
    note: z.string().max(1000).optional(),
});
// Shared envelope for all three admin actions — returns the refreshed consult.
export const AdminConsultActionResponse = z.object({
    consult: AdminConsultResponse,
});
// Merged consult timeline for the platform-admin side sheet (TEL-115 P2):
// status transitions (consult_status_history) interleaved with routing /
// decline / reroute / SLA-breach audit events, newest first. Every field is
// PHI-free: statuses, role labels, reason codes, specialty names, and provider
// (not patient) identity.
export const CONSULT_HISTORY_KINDS = [
    "status_change",
    "routing",
    "sla_breach",
    "decline",
    "reroute",
    "cancel",
    // TEL-282: a saved-but-unsent response draft was archived on a discard path.
    "discard",
];
export const ConsultHistoryKind = z.enum(CONSULT_HISTORY_KINDS);
export const ConsultHistoryEntry = z.object({
    at: z.string().datetime(),
    kind: ConsultHistoryKind,
    fromStatus: z.enum(LISTABLE_CONSULT_STATUSES).nullable(),
    toStatus: z.enum(LISTABLE_CONSULT_STATUSES).nullable(),
    actorRole: z.string().nullable(), // role at time of action; null = system
    // Display name of the human actor (provider identity — NOT patient PHI);
    // null for system actions or when the acting user was since deleted (TEL-282).
    actorDisplayName: z.string().nullable(),
    // Routing target (provider identity — NOT patient PHI). Populated for
    // routing/reroute entries so the sheet can show "routed to previously".
    specialistId: z.string().uuid().nullable(),
    specialistDisplayName: z.string().nullable(),
    detail: z.string().nullable(), // PHI-free label: reason code / specialty name / event label
});
export const ConsultHistoryResponse = z.object({
    entries: z.array(ConsultHistoryEntry),
});
// Internal notes — TEL-264. Append-only; visible to platform_admin + curators only.
export const InternalNoteResponse = z.object({
    id: z.string().uuid(),
    body: z.string(),
    authorId: z.string().uuid(),
    authorDisplayName: z.string(),
    createdAt: z.string().datetime(),
});
export const InternalNoteListResponse = z.object({
    notes: z.array(InternalNoteResponse),
});
export const CreateInternalNoteRequest = z.object({
    body: z.string().min(1).max(4000).trim(),
});
// Specialist queue item. Patient demographics are included (RLS-gated) but the
// UI only displays them for answered items (anti-bias: inbox is PHI-free).
export const SpecialistQueueItemResponse = z.object({
    id: z.string().uuid(),
    // "closed" is served only by GET /specialist/completed (TEL-197, formerly
    // /specialist/closed TEL-160); the live queue endpoint never returns it.
    status: z.enum([
        "assigned",
        "accepted",
        "answered",
        "follow-up-active",
        "clarification-requested",
        "closed",
    ]),
    specialtyId: z.string().uuid().nullable(),
    specialtyName: z.string().nullable(),
    slaDueAt: z.string().datetime().nullable(),
    acceptanceDueAt: z.string().datetime().nullable(),
    responseDueAt: z.string().datetime().nullable(),
    // 24h follow-up response deadline (TEL-15); set only on follow-up-active items.
    followupResponseDueAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    respondedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    // Close time (TEL-162); non-null only for status "closed". Sourced from the
    // consult's updatedAt, frozen at close since closed consults are immutable.
    closedAt: z.string().datetime().nullable(),
    // Patient age (full years) instead of the birth date — specialists see age +
    // sex only (TEL-181). Null pre-acceptance (TEL-144 redaction) or when the
    // birth date is unrecorded.
    patientAge: z.number().int().nonnegative().nullable(),
    patientSexAtBirth: SexAtBirth.nullable(),
    // True when the specialist has an in-progress response draft on this consult
    // (TEL-157, TC-2000 indicator gap). LEFT-JOIN existence on
    // consult_response_drafts, scoped to the requesting specialist — only ever
    // true on the live-queue path for accepted items.
    hasDraft: z.boolean(),
});
export const SpecialistQueueResponse = z.object({
    consults: z.array(SpecialistQueueItemResponse),
});
// Specialist Completed archive (TEL-197) — server-paginated: closed consults
// grow without bound. "completed" = when the specialist's part finished (see
// queries.ts SPECIALIST_COMPLETED_SORT_COLUMNS).
export const SPECIALIST_COMPLETED_SORT_KEYS = ["completed", "specialty"];
export const ListSpecialistCompletedQuery = z
    .object({
    // Exact specialty name (the filter dropdown's value).
    specialty: z.string().max(200).optional(),
})
    .merge(PageQuery)
    .merge(sortQuery(SPECIALIST_COMPLETED_SORT_KEYS, { sort: "completed", dir: "desc" }));
// `specialtyNames`: the filter dropdown's options — every specialty this
// specialist has a completed consult in, across the whole archive (not just the
// page being viewed).
export const SpecialistCompletedResponse = paginated(SpecialistQueueItemResponse).extend({
    specialtyNames: z.array(z.string()),
});
// Shared with the specialist decline dialog's maxLength/counter (see above).
export const DECLINE_DETAILS_MAX_LENGTH = 1000;
export const DeclineConsultRequest = z
    .object({
    reasonCode: DeclineReasonCode,
    details: z.string().trim().max(DECLINE_DETAILS_MAX_LENGTH).optional(),
})
    .superRefine((value, ctx) => {
    if (value.reasonCode === "other" && !value.details) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["details"],
            message: "Additional details are required when the reason is Other.",
        });
    }
});
export const RerouteConsultRequest = z.object({
    targetSpecialtyId: z.string().uuid("targetSpecialtyId must be a UUID"),
    reason: z.string().min(1, "reason is required"),
});
export const SurfaceToAdminRequest = z.object({
    reason: z.string().min(1, "reason is required"),
});
export const RespondConsultRequest = z.object({
    responseText: z.string().min(1, "responseText is required"),
    // At least one structured outcome tag is required to submit (TEL-85, TC-223).
    // Duplicates are de-duplicated in the query layer before insert.
    outcomeTags: z
        .array(OutcomeTagCode.exclude(["clarifying-question"]))
        .nonempty("at least one outcome tag is required"),
});
// Specialist response draft autosave (TEL-157). Deliberately permissive where
// RespondConsultRequest is strict — a draft is partial by nature, so
// responseText may be null/empty and outcomeTags may be []. .strict() rejects
// unknown keys so a stale client can't smuggle extra fields into the upsert.
export const SaveResponseDraftRequest = z
    .object({
    responseText: z.string().max(20000).nullable(),
    outcomeTags: z.array(OutcomeTagCode),
})
    .strict();
// PCP/CSS manual close-out (TEL-84). Both yes/no answers are required; starRating
// is null (unrated, the default) or an integer 1–5. .strict() rejects unknown keys.
export const CloseConsultRequest = z
    .object({
    changedManagement: z.boolean(),
    referralNeeded: z.boolean(),
    starRating: z.number().int().min(1).max(5).nullable(),
})
    .strict();
// ─── Attachments (TEL-79) ────────────────────────────────────────────────────
// WebP excluded — neither OS clipboard screenshots nor phone cameras nor EMR
// exports produce WebP; per TC-1211, sources are PNG/JPEG/HEIC/TIFF + PDF.
//
// HEIC/HEIF deliberately absent (TEL-99). The scanner's prebuilt sharp/libvips
// decodes HEIF only with AV1 compression (AVIF) — the HEVC decoder is excluded
// upstream for patent-licensing reasons, so default iPhone photos (HEVC HEIC)
// throw "Input buffer contains unsupported image format" in stripExif and would
// be quarantined as scan-failed. Re-adding HEIC requires a custom libvips build
// with HEVC support (+ licensing review) — tracked with the QR phone-capture
// epic (TC-704 / TC-1246). Keep this list and SHARP_CONTENT_TYPES in sync.
export const ATTACHMENT_MIME_ALLOWLIST = [
    "image/jpeg",
    "image/png",
    "image/tiff",
    "application/pdf",
];
// 25 MiB cap. Single-shot PUT is sufficient at this size; resumable is a
// follow-up if/when ceilings change.
export const ATTACHMENT_MAX_BYTES = 26_214_400;
// Per-consult attachment count cap. Bounds the list response without
// pagination; single source for both the client-side check in the upload UIs
// and the server-side check in issueUploadUrl (under a consult-row lock so
// concurrent issuances can't race past it). Submitters who hit this can
// delete an existing chip and retry.
export const ATTACHMENT_PER_CONSULT_MAX = 25;
export const ATTACHMENT_SCAN_STATES = ["scanning", "clean", "blocked", "failed"];
export const IssueUploadUrlRequest = z.object({
    filename: z.string().min(1).max(255),
    contentType: z.string(),
    sizeBytes: z.number().int().positive(),
    // TEL-15: when present, links the upload to a follow-up message on this consult.
    // The service validates the message belongs to :consultId and the consult is in
    // the follow-up window.
    messageId: z.string().uuid().optional(),
});
export const IssueUploadUrlResponse = z.object({
    attachmentId: z.string().uuid(),
    uploadUrl: z.string().url(),
    gcsObjectKey: z.string(),
    expiresAt: z.string().datetime(),
});
export const AttachmentResponse = z.object({
    id: z.string().uuid(),
    consultId: z.string().uuid(),
    messageId: z.string().uuid().nullable(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    scanState: z.enum(ATTACHMENT_SCAN_STATES),
    pages: z.number().int().nullable(),
    uploadedAt: z.string().datetime(),
    scanCompletedAt: z.string().datetime().nullable(),
    uploadedBy: z.string().uuid(),
    // Uploader's users.role at read time — string (not enum) to match the
    // role precedent in UserResponse. Web groups submitter vs specialist on it.
    uploadedByRole: z.string(),
});
export const ListAttachmentsResponse = z.array(AttachmentResponse);
// ─── Phone capture sessions (TEL-120) ────────────────────────────────────────
// Capture-session token lifetime. Short by design: the QR modal shows a
// countdown and re-mints on demand, and a leaked URL goes stale fast.
export const CAPTURE_SESSION_TTL_MINUTES = 10;
// Images only — the phone page has no PDF story. Subset of
// ATTACHMENT_MIME_ALLOWLIST so everything the phone can send survives the
// scanner pipeline. HEIC stays excluded: iOS transcodes both camera captures
// and library picks to JPEG when the accept list omits it.
export const CAPTURE_MIME_ALLOWLIST = ["image/jpeg", "image/png", "image/tiff"];
export const CaptureSessionCreateRequest = z.object({
    sendSms: z.boolean().optional(),
    // Honored only with sendSms: saved to the user's profile (users.phone) before
    // the link is sent, so subsequent consults skip the input.
    smsPhone: usPhone.optional(),
});
export const CaptureSessionCreateResponse = z.object({
    captureUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    phoneOnFile: z.boolean(),
    smsSent: z.boolean(),
});
export const CaptureSessionInfoResponse = z.object({
    expiresAt: z.string().datetime(),
    uploadedCount: z.number().int().nonnegative(),
});
// IssueUploadUrlRequest minus messageId (no follow-up linkage from the phone),
// with the same field constraints.
export const CaptureUploadUrlRequest = z.object({
    filename: z.string().min(1).max(255),
    contentType: z.string(),
    sizeBytes: z.number().int().positive(),
});
// Same wire shape as the authenticated upload-url response.
export const CaptureUploadUrlResponse = IssueUploadUrlResponse;
// Trimmed AttachmentResponse for the unauthenticated phone page: no consultId,
// uploader identity, or pages — only what the page needs to render its chips.
export const CaptureAttachmentResponse = z.object({
    id: z.string().uuid(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    scanState: z.enum(ATTACHMENT_SCAN_STATES),
    uploadedAt: z.string().datetime(),
});
// ─── Follow-up messages (TEL-15) ─────────────────────────────────────────────
export const PostFollowupMessageRequest = z
    .object({
    body: z.string().min(1).max(5000),
    // Only the assigned specialist may send 'request_info' (a clarifying
    // question); the server coerces any other sender to 'reply'. Absent ⇒
    // 'reply'. request_info is excluded from the specialist_responses pay
    // surface — TEL-15 follow-up (TC-1773/TC-1774).
    messageKind: z.enum(["reply", "request_info"]).optional(),
})
    .strict();
export const FollowupMessageResponse = z.object({
    id: z.string().uuid(),
    consultId: z.string().uuid(),
    authorId: z.string().uuid(),
    authorName: z.string(),
    senderRole: z.enum(["pcp", "clinical_support_staff", "consult_curator", "specialist"]),
    messageKind: z.enum(["reply", "request_info"]),
    body: z.string(),
    createdAt: z.string().datetime(),
    // Populated from Phase 3 onward; the wire contract carries the field from day one.
    attachments: z.array(AttachmentResponse).default([]),
});
export const ListFollowupMessagesResponse = z.object({
    messages: z.array(FollowupMessageResponse),
});
export const IssueDownloadUrlResponse = z.object({
    downloadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
});
// ── Specialist handbook admin publishing (TEL-219) ──────────────────────────
// contentType is fixed server-side to application/pdf — the handbook is always
// a PDF, so the client only supplies the size for the signed-PUT constraint.
export const HandbookUploadUrlRequest = z.object({
    sizeBytes: z.number().int().positive(),
});
export const HandbookUploadUrlResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string(),
    expiresAt: z.string().datetime(),
});
// objectKey must be one the upload-url endpoint minted (handbook/<uuid>.pdf);
// the regex also admits the operator-published gsutil convention
// (handbook/v1_2.pdf) so out-of-band uploads can be registered via SQL only.
export const PublishHandbookRequest = z.object({
    objectKey: z.string().regex(/^handbook\/[A-Za-z0-9._-]+\.pdf$/),
    label: z.string().min(1).max(80),
});
export const HandbookVersionResponse = z.object({
    id: z.string().uuid(),
    label: z.string(),
    objectKey: z.string(),
    createdAt: z.string().datetime(),
    // Display name of the publishing admin; null for out-of-band (gsutil + SQL)
    // publishes recorded before the in-app flow existed.
    publishedByName: z.string().nullable(),
    isLive: z.boolean(),
});
// ─── Consult PDF export (TEL-110) ────────────────────────────────────────────
// Exactly two PDFs per consult: the specialist's initial response, and a final
// snapshot at close. Stored as immutable artifacts in the generated-PDF bucket.
export const CONSULT_PDF_KINDS = ["initial_response", "final"];
// Immutable, download-only artifact. EMR-upload state is NOT carried here — it
// lives on the consult (emrInitialUploadedAt / emrCloseUploadedAt, TEL-114).
export const ConsultPdfResponse = z.object({
    id: z.string().uuid(),
    consultId: z.string().uuid(),
    kind: z.enum(CONSULT_PDF_KINDS),
    generatedAt: z.string().datetime(),
    sizeBytes: z.number().int().nonnegative(),
});
export const ListConsultPdfsResponse = z.array(ConsultPdfResponse);
// ─── Dashboards (TEL-18) ─────────────────────────────────────────────────────
// Platform-admin business dashboard summary — trailing-30-day, cross-org
// aggregates. Counts/aggregates only, zero PHI (TC-143). `averageRating` is the
// only nullable field (null when no rated closeouts in window).
// One specialty row in the specialty breakdown — the count of consults submitted
// under that specialty in the caller's org during the selected window
// (TEL-18 P2; shared by practice-admin and org drill-down).
export const SpecialtyBreakdownItem = z.object({
    specialtyId: z.string().uuid(),
    specialtyName: z.string(),
    count: z.number().int(),
});
// Query params for the platform-admin dashboard — optional date window for the
// Reporting band. Status/SLA tiles always use the hardcoded trailing-30-day
// window; from/to govern only activity, value, outcomeTagDistribution, and
// orgBreakdown. Defaults to trailing 30 days when omitted.
export const PlatformAdminDashboardQuery = z.object({
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// One specialty row in the platform-admin Consults by Specialty table (TEL-NEW51).
// consultsCompleted/medianTurnaroundDays match the summary-band definitions.
// declinesCount / firstAssignmentsCount are raw counts so the UI can display
// "X / Y" or a derived percentage without losing precision.
export const PlatformSpecialtyBreakdownItem = z.object({
    specialtyId: z.string().uuid(),
    specialtyName: z.string(),
    consultsCompleted: z.number().int(),
    medianTurnaroundDays: z.number().nullable(),
    declinesCount: z.number().int(),
    firstAssignmentsCount: z.number().int(),
});
export const PlatformAdminOrgBreakdownItem = z.object({
    orgId: z.string().uuid(),
    orgName: z.string(),
    isActive: z.boolean(),
    consultsCompleted: z.number().int(),
    activePcps: z.number().int(),
    totalPcps: z.number().int(),
    lastActivity: z.string().nullable(),
});
// Org drill-down response for the platform-admin org detail page (TEL-NEW51).
// Two layers: public-facing practice-admin style metrics and an internal strip
// only the platform admin sees.
export const PlatformAdminOrgDashboardResponse = z.object({
    windowStart: z.string(),
    windowEnd: z.string(),
    activity: z.object({
        completed: z.number().int(),
        medianTurnaroundDays: z.number().nullable(),
    }),
    value: z.object({
        referralsAvoided: z.number().int(),
        managementChanged: z.number().int(),
        closeoutCount: z.number().int(),
    }),
    specialtyBreakdown: z.array(SpecialtyBreakdownItem),
    // Internal strip — not shown to the practice admin.
    internal: z.object({
        averageRating: z.number().nullable(),
        activePcps: z.number().int(),
        totalPcps: z.number().int(),
        lastActivity: z.string().nullable(),
    }),
});
export const PlatformAdminDashboardResponse = z.object({
    windowDays: z.number().int().positive(),
    // ISO-8601 timestamp of when the server assembled this snapshot. Used by the
    // dashboard UI freshness stamp ("Last updated: …").
    dataAsOf: z.string(),
    status: z.object({
        total: z.number().int(),
        pending: z.number().int(),
        // Lifecycle states surfaced as individual tiles in the dashboard (TEL-320).
        clarificationRequested: z.number().int(),
        answered: z.number().int(),
        followUpActive: z.number().int(),
        closed: z.number().int(),
        // Action-required queue depths — unwindowed (any submission age), matching
        // the All Consults views they deep-link to (TEL-251/TEL-320).
        unassigned: z.number().int(),
        surfaced: z.number().int(),
        rerouted: z.number().int(),
    }),
    sla: z.object({
        acceptanceWarnings: z.number().int(),
        responseBreaches: z.number().int(),
        followupBreaches: z.number().int(),
    }),
    // Reporting-band metrics — governed by the user-selected date range (default
    // trailing 30 days). Unlike status/sla, these reflect the chosen window.
    activity: z.object({
        completed: z.number().int(),
        medianTurnaroundDays: z.number().nullable(),
    }),
    value: z.object({
        referralsAvoided: z.number().int(),
        managementChanged: z.number().int(),
        averageRating: z.number().nullable(),
        closeoutCount: z.number().int(),
    }),
    // Outcome-tag distribution over the reporting window (TEL-85). One entry per
    // tag with ≥1 occurrence; empty array when none.
    outcomeTagDistribution: z.array(z.object({ tag: OutcomeTagCode, count: z.number().int() })),
    // Per-specialty table for the reporting band (TEL-NEW51). Completed +
    // turnaround + decline rate per specialty, date-range-dynamic. Supersedes
    // the simpler count-only version merged via NEW-32.
    specialtyBreakdown: z.array(PlatformSpecialtyBreakdownItem),
    // Per-org health vitals for the By Organization table (TEL-NEW51).
    // Supersedes the simpler count-only version merged via TEL-310/NEW-33.
    orgBreakdown: z.array(PlatformAdminOrgBreakdownItem),
});
// Query params for the practice-admin dashboard — optional date window;
// defaults to current-month-start → now on the server.
export const PracticeAdminDashboardQuery = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
});
// Practice-admin business dashboard summary — own-org (RLS), configurable
// date window (defaults to month-to-date). Activity group: Consults Completed
// (billable; anchored on responded_at) + median turnaround in days. Value group:
// confirmed outcomes from PCP close-outs (referralsAvoided, managementChanged)
// + closeoutCount for rate and coverage denominators. Specialty breakdown totals
// match activity.completed. No avg-rating tile (platform dashboard only). Zero PHI.
export const PracticeAdminDashboardResponse = z.object({
    windowStart: z.string(),
    windowEnd: z.string(),
    activity: z.object({
        completed: z.number().int(),
        medianTurnaroundDays: z.number().nullable(),
    }),
    value: z.object({
        referralsAvoided: z.number().int(),
        managementChanged: z.number().int(),
        closeoutCount: z.number().int(),
    }),
    specialtyBreakdown: z.array(SpecialtyBreakdownItem),
});
// Submitter (pcp / css / curator) workflow metrics (NEW-8) — own-org (RLS),
// month-to-date value snapshot. No specialty breakdown, no admin-level detail.
export const SubmitterDashboardResponse = z.object({
    windowLabel: z.literal("month-to-date"),
    value: z.object({
        patientsHelped: z.number().int(),
        referralsAvoided: z.number().int(),
        managementChanged: z.number().int(),
    }),
});
// Specialist workflow dashboard summary (TEL-18 P3) — current-state counts for
// the calling specialist's three workflow destinations. No time window, no PHI:
// counts only. Awaiting response includes both initial responses and follow-ups;
// Completed includes answered, clarification-requested, and closed consults.
// activeConsults is the nonterminal assignment count used when a specialist
// pauses availability; outstanding clarifications remain assigned and may
// return to the specialist after the submitter replies.
export const SpecialistDashboardResponse = z.object({
    inbox: z.number().int(),
    awaitingResponse: z.number().int(),
    completed: z.number().int(),
    activeConsults: z.number().int(),
});
// GET /v1/dashboard/specialist/metrics?monthStart= (Platform Testing 2): the
// specialist's own performance. `monthStart` is the start of "this month" in
// the viewer's timezone (an ISO instant), so the month boundary matches what
// they see. Durations are minutes; null when there's nothing to measure yet.
export const SpecialistMetricsQuery = z.object({ monthStart: z.string().datetime() });
export const SpecialistMetricsResponse = z.object({
    completedAllTime: z.number().int(),
    completedThisMonth: z.number().int(),
    // 0..1 — acceptances / (acceptances + declines incl. acceptance timeouts).
    acceptanceRate: z.number().nullable(),
    medianTimeToAcceptMinutes: z.number().nullable(),
    medianResponseMinutes: z.number().nullable(),
    medianFollowupResponseMinutes: z.number().nullable(),
});
// ─── Audit log (TEL-107) ─────────────────────────────────────────────────────
// The complete audit_log action vocabulary. Single source of truth: the db
// package derives its AuditAction type from this array, and the /audit-logs UI
// derives its action-group filter from it — adding a value here is the only
// step needed for both.
export const AUDIT_ACTIONS = [
    "routing.assigned",
    "routing.no_candidates",
    "routing.error",
    "routing.acceptance_expired",
    // TEL-182: specialist accepted but never answered within the response window;
    // the sla-scan sweep returned the consult to routing (specialist excluded).
    "routing.response_expired",
    "routing.manual_assigned",
    // TEL-158: a specialist re-enabled availability, so their stale decline
    // exclusion on a stuck consult was cleared and the consult re-routed.
    "routing.decline_exclusion_cleared",
    // TEL-158: a specialist re-enabled availability, so a consult auto-routing had
    // parked for admin purely because no specialist was available (no_candidates)
    // was un-parked and re-routed now that the specialist is a candidate again.
    "routing.no_candidates_recovered",
    "attachment.upload_url_issued",
    "attachment.deleted",
    "attachment.download_url_issued",
    "attachment.downloaded",
    // TEL-107: the sweep promoting a clean-scanned attachment into the `clean`
    // bucket is a PHI-object state change and must appear in the trail.
    "attachment.scan_promoted",
    // TEL-120: QR phone capture. created = a capture session was minted (or
    // re-minted, superseding the prior token); redeemed = the first time a
    // minted token was successfully resolved by the unauthenticated phone page.
    "capture_session.created",
    "capture_session.redeemed",
    "consult_pdf.generated",
    // TEL-110: PDFs are streamed through the API now, so we audit the actual
    // download rather than (identity-less) signed-URL issuance.
    "consult_pdf.downloaded",
    "consult.created",
    "consult.draft_updated",
    "consult.draft_deleted",
    "consult.submitted",
    "consult.accepted",
    "consult.declined",
    "consult.rerouted",
    // TEL-115: platform-admin manual superpowers. reassigned = new specialist,
    // same specialty; rerouted (above) is reused for an admin specialty change;
    // cancelled = admin moved the consult to the terminal cancelled state.
    "consult.reassigned",
    // Distinct from consult.reassigned above: restricted to follow-up-active
    // consults, where "already answered" is implicit rather than an admin
    // acknowledgement flag.
    "consult.followup_reassigned",
    "consult.cancelled",
    "consult.surfaced_to_admin",
    "consult.response_submitted",
    // TEL-282: a saved-but-unsent response draft was archived + removed by one of
    // the six discard paths (decline / reroute / surface-to-admin / admin
    // reassign / admin cancel / specialist deactivation). One row per archived
    // draft row; the draft text itself lives in consult_response_draft_archives,
    // never in this PHI-free payload.
    "consult.response_draft_discarded",
    "consult.followup_message_posted",
    "consult.clarification_requested",
    "consult.clarification_answered",
    "consult.closed",
    "consult.force_closed",
    "consult.auto_closed",
    "consult.viewed",
    // TEL-175: the follow-up thread is separately fetchable clinical content, so
    // reading it is its own detail-grain ePHI read event (HIPAA §164.312(b)).
    "consult.messages_viewed",
    "consult.emr_initial_toggled",
    "consult.emr_close_toggled",
    "consult.exported",
    "curator_org.granted",
    "curator_org.revoked",
    "patient.created",
    "patient.viewed",
    // TEL-107 audit gap pass: user lifecycle + access-management events
    // (§164.308). subjectId is the target user; organizationId is set when the
    // operation is org-scoped (member management) and null for internal users.
    "user.invited",
    "user.invite_accepted",
    "user.deactivated",
    "user.reactivated",
    "user.reinvited",
    "user.password_reset_sent",
    "user.password_changed",
    "user.mfa_reset",
    "user.mfa_enrollment_restarted",
    "user.mfa_enrolled",
    // Specialist availability — "availability" IS the accepting-consults flag;
    // there is no separate availability field.
    "user.accepting_consults_changed",
    "user.specialty_added",
    "user.specialty_removed",
    // NEW-21: platform admin corrects a typo'd invite email.
    "user.email_changed",
    // Platform Testing 2: a platform / practice admin renames a user.
    "user.display_name_changed",
    // TEL-107: exporting the audit log itself is audited (one row per export).
    "audit_log.exported",
    // TEL-219: the specialist handbook carries contract-referenced pay terms, so
    // every publish action is audited. upload_url_issued mirrors the attachment
    // audit-before-issuance pattern; version_published = a new upload went live;
    // version_reverted = an admin re-published a prior version (insert-only —
    // rows are never deleted, so the trail shows every live-version change).
    "handbook.upload_url_issued",
    "handbook.version_published",
    "handbook.version_reverted",
    // Deleting removes the document's pointer rows from the version list only —
    // the GCS object is retained and this event carries the full row payload, so
    // what-was-live-when stays reconstructable from the audit trail alone.
    "handbook.version_deleted",
    "organization.updated",
];
// `action` accepts a single value (?action=consult.viewed) or a comma-separated
// list — same preprocessing shape as consultStatusListParam. Plain strings, not
// an enum: the AuditAction union lives in @telecurb/db and evolves; an unknown
// value simply matches no rows.
const auditActionListParam = z.preprocess((val) => (typeof val === "string" ? val.split(",").map((s) => s.trim()) : val), z.array(z.string().min(1).max(100)).min(1));
// One audit_log row on the wire. State payloads are already PHI-free at write
// time (TC-45: opaque IDs, status strings, field-name lists), so they ship
// as-is for the platform-admin surface.
export const AuditLogEventResponse = z.object({
    id: z.string().uuid(),
    at: z.string().datetime(),
    action: z.string(),
    actorId: z.string().uuid().nullable(),
    actorDisplayName: z.string().nullable(),
    actorRole: z.string().nullable(),
    organizationId: z.string().uuid().nullable(),
    subjectType: z.string(),
    subjectId: z.string().nullable(),
    previousState: z.record(z.unknown()).nullable(),
    newState: z.record(z.unknown()).nullable(),
});
// GET /v1/audit-logs — keyset-paginated, newest first. `cursor` is opaque
// (minted by the API in the prior page's nextCursor); an undecodable cursor is
// a 400. from/to are optional here (the UI defaults to the last 7 days) but
// required on the export.
export const ListAuditLogsQuery = z
    .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    action: auditActionListParam.optional(),
    actorId: z.string().uuid().optional(),
    actorRole: z.string().max(50).optional(),
    subjectId: z.string().max(200).optional(),
    orgId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(200).optional(),
})
    .refine((q) => !q.from || !q.to || Date.parse(q.from) <= Date.parse(q.to), {
    message: "from must be before to",
    path: ["from"],
});
export const ListAuditLogsResponse = z.object({
    events: z.array(AuditLogEventResponse),
    nextCursor: z.string().nullable(),
});
// GET /v1/audit-logs/export — CSV. from/to REQUIRED (TC-1463: the audit dataset
// is too large to export unscoped); everything else mirrors the list query.
export const ExportAuditLogsQuery = z
    .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    action: auditActionListParam.optional(),
    actorId: z.string().uuid().optional(),
    actorRole: z.string().max(50).optional(),
    subjectId: z.string().max(200).optional(),
    orgId: z.string().uuid().optional(),
})
    .refine((q) => Date.parse(q.from) <= Date.parse(q.to), {
    message: "from must be before to",
    path: ["from"],
});
const positiveInt = z.number().int().positive();
export const PlatformSlaSettingsResponse = z.object({
    slaHoursAtCreation: positiveInt,
    acceptanceWindowHours: positiveInt,
    responseWindowHours: positiveInt,
    followupResponseWindowHours: positiveInt,
    consultAutoCloseWindowDays: positiveInt,
    adminSlaWarningHours: positiveInt,
    adminStuckRoutingRealertHours: positiveInt,
    fileScanTimeoutMinutes: positiveInt,
    updatedAt: z.string().datetime().nullable(),
});
export const UpdatePlatformSlaSettingsRequest = z
    .object({
    slaHoursAtCreation: positiveInt.optional(),
    acceptanceWindowHours: positiveInt.optional(),
    responseWindowHours: positiveInt.optional(),
    followupResponseWindowHours: positiveInt.optional(),
    consultAutoCloseWindowDays: positiveInt.optional(),
    adminSlaWarningHours: positiveInt.optional(),
    adminStuckRoutingRealertHours: positiveInt.optional(),
    fileScanTimeoutMinutes: positiveInt.optional(),
})
    .strict();
//# sourceMappingURL=dto.js.map